import imageCompression from 'browser-image-compression'

import type {
  DoesUserExistDTOType,
  DoesUserExistResponseType,
  LoginDTOType,
  LoginMethodResponseType,
  LoginResponseType,
  MfaDisableDTOType,
  MfaEnableDTOType,
  MfaSetupResponseType,
  MfaVerifyDTOType,
  MfaVerifyResponseType,
  RegisterDTOType,
  ResetPasswordDTOType,
  UpdateUserDescriptionDTOType,
  UpdateUserDTOType,
  V1AccountDataType,
  V1AuthUserType,
  V1ChangePasswordDTOType,
  V1PhoneType,
  V1UpdateLanguageDTOType,
  V1UpdatePhoneDTOType,
  V1UpdateUserDTOType,
  V1UpdateUserIdentityDTOType,
  V1UpdateUserPictureDTOType,
} from '@/features/auth/types'
import fetchClient from '@/lib/fetch'

export async function login(data: LoginDTOType): Promise<LoginResponseType> {
  return fetchClient('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email: data.email, password: data.password }),
  })
}

export async function register(data: RegisterDTOType): Promise<void> {
  return fetchClient('/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function forgotPassword(email: string): Promise<void> {
  return fetchClient('/v1/auth/forgotPassword', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export async function resetPasswordMutation(data: ResetPasswordDTOType): Promise<void> {
  return fetchClient('/v1/auth/updateForgotPassword', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export const V1_AUTH_USER_QUERY_KEY = 'v1-auth-user'
export async function V1GetAuthUser(): Promise<V1AuthUserType> {
  return fetchClient('/v1/auth/user', {
    method: 'GET',
  })
}

export const V1_PHONE_QUERY_KEY = 'v1-phone'
// TODO: user id does not exist
export async function V1GetPhone(userId: string): Promise<V1PhoneType> {
  return fetchClient(`/v1/user/${userId}/phone`, {
    method: 'GET',
  })
}

export const V1_USER_DATA_QUERY_KEY = 'v1-user-data'
export async function getUserData(): Promise<V1AccountDataType> {
  return fetchClient('/v1/user', { method: 'GET' })
}

export async function V1updateUser(data: V1UpdateUserDTOType): Promise<void> {
  return fetchClient('/v1/user/user', {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function V1updateUserIdentity(data: V1UpdateUserIdentityDTOType): Promise<void> {
  return fetchClient('/v1/user/identity', {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function doesUserExistMutation(data: DoesUserExistDTOType): Promise<DoesUserExistResponseType> {
  return fetchClient(`/v1/auth/exist`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function V1UpdateUserPhone(data: V1UpdatePhoneDTOType) {
  return fetchClient(`/v1/user/${data.userId}/phone`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function V1updateUserLanguage(data: V1UpdateLanguageDTOType) {
  return fetchClient('/v1/user/language', {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function V1UpdateUserPicture(data: V1UpdateUserPictureDTOType) {
  return fetchClient(
    '/v1/user/profil/picture',
    {
      method: 'PATCH',
      body: data,
    },
    true,
  )
}

export async function V1UpdateUserDescriptionMutation(data: UpdateUserDescriptionDTOType) {
  return fetchClient(`/v1/user/${data.userId}/description`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateUserMutation(data: UpdateUserDTOType): Promise<void> {
  if (data.email) {
    return V1updateUser({
      email: data.email,
    })
  }

  if (data.firstName && data.lastName) {
    return V1updateUserIdentity({
      firstname: data.firstName,
      lastname: data.lastName,
    })
  }

  if (data.phone && data.userId) {
    return V1UpdateUserPhone({
      phone: data.phone,
      userId: data.userId,
    })
  }

  if (data.language) {
    return V1updateUserLanguage({
      language: data.language,
    })
  }

  if (data.picture) {
    // compress the file
    const compressedFile = await imageCompression(data.picture, {
      maxSizeMB: 0.29,
      maxWidthOrHeight: 400,
    })

    // formdata
    const formData = new FormData()
    formData.append('files', compressedFile)
    return V1UpdateUserPicture(formData)
  }
}

export async function sendEmailForgotPasswordMutation(email: string): Promise<void> {
  return fetchClient('/v1/auth/forgotPassword', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export async function changePasswordMutation({
  userId,
  oldPassword,
  password,
  confirmPassword,
}: V1ChangePasswordDTOType): Promise<void> {
  return fetchClient(`/v1/auth/password/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ oldPassword, password, confirmPassword }),
  })
}

export async function fetchLoginMethod(email: string): Promise<LoginMethodResponseType> {
  // encodeURIComponent : sans lui, un « + » dans l'adresse devient une espace
  // dans la query string, zod refuse l'adresse, et l'appel échoue en 400.
  return fetchClient(`/v1/auth/login-method?email=${encodeURIComponent(email)}`, {
    method: 'GET',
  })
}

/** `skippedSso` : comptes SSO, qui ne reçoivent pas d'invitation (KC-493). */
export async function sendPasswords(
  programId: string,
  userIds: string[],
): Promise<{ sent: number; skippedSso: number }> {
  return fetchClient('/v1/auth/send-passwords', {
    method: 'POST',
    body: JSON.stringify({ programId, users: userIds }),
  })
}

export async function getInvitation(token: string): Promise<{ email: string; expiresAt: string }> {
  return fetchClient(`/v1/auth/invitation/${encodeURIComponent(token)}`, {
    method: 'GET',
  })
}

export async function acceptInvitationMutation(data: {
  token: string
  password: string
  confirmPassword: string
}): Promise<{ email: string }> {
  return fetchClient('/v1/auth/accept-invitation', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function addLoginHistoryMutation() {
  return fetchClient('/v1/auth/add-login-history', {
    method: 'POST',
  })
}

// MFA TOTP
export async function mfaSetup(): Promise<MfaSetupResponseType> {
  return fetchClient('/v1/auth/mfa/setup', {
    method: 'POST',
  })
}

export async function mfaEnable(data: MfaEnableDTOType): Promise<void> {
  return fetchClient('/v1/auth/mfa/enable', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function mfaDisable(data: MfaDisableDTOType): Promise<void> {
  return fetchClient('/v1/auth/mfa/disable', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function mfaVerifyLogin(data: MfaVerifyDTOType): Promise<MfaVerifyResponseType> {
  return fetchClient('/v1/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}
